import React, { useCallback, useState } from 'react';
import { Briefcase, Save, RefreshCw, Scale, Plane, Package, Check, Plus, Minus } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import AirlineParamsSection from './AirlineParamsSection';
import PassengerParamsSection from './PassengerParamsSection';
import BaggageParamsSection from './BaggageParamsSection';
import ExtraItemsSection from './ExtraItemsSection';
import LoyaltyParamsSection from './LoyaltyParamsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент основной формы расчета багажа
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  airlinesData,
  travelClasses,
  destinationTypes,
  passengerTypes,
  extraItemsList,
  loyaltyStatuses,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Автоматический сброс статуса лояльности при снятии галочки
      if (name === 'hasLoyaltyStatus' && !checked) {
        newState.loyaltyStatus = 'standard';
      }
      
      // Сброс списка дополнительных предметов если отключен флаг
      if (name === 'hasExtraItems' && !checked) {
        newState.extraItems = [];
      }
      
      // Сброс перевеса если отключен флаг
      if (name === 'includeOverweight' && !checked) {
        newState.overweightKg = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);
  
  // Обработчик переключения значений в списке дополнительных предметов
  const handleExtraItemToggle = useCallback((itemId) => {
    setFormData(prev => {
      // Если элемент уже включен - удаляем его
      if (prev.extraItems.includes(itemId)) {
        return {
          ...prev,
          extraItems: prev.extraItems.filter(id => id !== itemId)
        };
      } 
      // Иначе - добавляем
      else {
        return {
          ...prev,
          extraItems: [...prev.extraItems, itemId]
        };
      }
    });
  }, [setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      airline, travelClass, destinationType, passengerType,
      baggageWeight, carryOnWeight, carryOnCount, baggageCount,
      hasLoyaltyStatus, loyaltyStatus,
      includeOverweight, overweightKg
    } = formData;

    // Валидация авиакомпании
    if (!airline) {
      newErrors.airline = 'Выберите авиакомпанию';
    }
    
    // Валидация класса обслуживания
    if (!travelClass) {
      newErrors.travelClass = 'Выберите класс обслуживания';
    }
    
    // Валидация типа направления
    if (!destinationType) {
      newErrors.destinationType = 'Выберите тип направления';
    }
    
    // Валидация типа пассажира
    if (!passengerType) {
      newErrors.passengerType = 'Выберите тип пассажира';
    }

    // Парсинг и валидация числовых значений
    const numBaggageWeight = parseFloatOrDefault(baggageWeight, -1);
    const numCarryOnWeight = parseFloatOrDefault(carryOnWeight, -1);
    const numCarryOnCount = parseIntOrDefault(carryOnCount, -1);
    const numBaggageCount = parseIntOrDefault(baggageCount, -1);
    const numOverweightKg = parseFloatOrDefault(overweightKg, -1);

    // Валидация веса багажа
    if (!isPositiveNumber(baggageWeight) || numBaggageWeight <= 0) {
      newErrors.baggageWeight = 'Введите положительное число';
    } else if (numBaggageWeight > 100) {
      newErrors.baggageWeight = 'Вес багажа не может превышать 100 кг';
    }
    
    // Валидация веса ручной клади
    if (!isPositiveNumber(carryOnWeight) || numCarryOnWeight <= 0) {
      newErrors.carryOnWeight = 'Введите положительное число';
    } else if (numCarryOnWeight > 30) {
      newErrors.carryOnWeight = 'Вес ручной клади не может превышать 30 кг';
    }
    
    // Валидация количества мест ручной клади
    if (!isInteger(carryOnCount) || numCarryOnCount <= 0) {
      newErrors.carryOnCount = 'Введите целое положительное число';
    } else if (numCarryOnCount > 5) {
      newErrors.carryOnCount = 'Количество мест не может превышать 5';
    }
    
    // Валидация количества мест багажа
    if (!isInteger(baggageCount) || numBaggageCount <= 0) {
      newErrors.baggageCount = 'Введите целое положительное число';
    } else if (numBaggageCount > 10) {
      newErrors.baggageCount = 'Количество мест не может превышать 10';
    }
    
    // Валидация статуса лояльности
    if (hasLoyaltyStatus && !loyaltyStatus) {
      newErrors.loyaltyStatus = 'Выберите статус программы лояльности';
    }
    
    // Валидация перевеса
    if (includeOverweight) {
      if (!isPositiveNumber(overweightKg) || numOverweightKg <= 0) {
        newErrors.overweightKg = 'Введите положительное число';
      } else if (numOverweightKg > 50) {
        newErrors.overweightKg = 'Превышение не может быть более 50 кг';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета параметров багажа
  const calculateLuggage = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготовка входных данных ---
        const {
          airline, travelClass, destinationType, passengerType,
          baggageWeight, carryOnWeight, carryOnCount, baggageCount,
          hasLoyaltyStatus, loyaltyStatus,
          hasExtraItems, extraItems,
          includeOverweight, overweightKg
        } = formData;

        // Парсинг числовых значений
        const numBaggageWeight = parseFloatOrDefault(baggageWeight);
        const numCarryOnWeight = parseFloatOrDefault(carryOnWeight);
        const numCarryOnCount = parseIntOrDefault(carryOnCount);
        const numBaggageCount = parseIntOrDefault(baggageCount);
        const numOverweightKg = includeOverweight ? parseFloatOrDefault(overweightKg, 0) : 0;

        // Получение данных по выбранной авиакомпании
        const airlineData = airlinesData.find(a => a.id === airline) || airlinesData[0];
        
        // Получение норм багажа для выбранных параметров
        const baggageAllowance = airlineData.baggageAllowance[destinationType][travelClass];
        
        // Получение бонусов программы лояльности
        const loyaltyBonus = hasLoyaltyStatus 
          ? airlineData.loyaltyProgram[loyaltyStatus] 
          : { extraWeight: 0, extraCount: 0 };
        
        // Расчет всех специальных условий
        const specialConditions = {};
        if (passengerType === 'infant' || passengerType === 'child') {
          specialConditions.passengerType = airlineData.specialConditions[passengerType];
        }
        
        // --- Расчет итоговых допустимых масс и количеств ---
        
        // Расчет допустимой массы основного багажа
        const allowedBaggageWeight = baggageAllowance.weight + loyaltyBonus.extraWeight;
        
        // Расчет допустимого количества мест основного багажа
        const allowedBaggageCount = baggageAllowance.count + loyaltyBonus.extraCount;
        
        // Расчет допустимой массы ручной клади
        const allowedCarryOnWeight = baggageAllowance.carryOnWeight;
        
        // Расчет допустимого количества мест ручной клади
        const allowedCarryOnCount = baggageAllowance.carryOnCount;
        
        // --- Расчет превышений ---
        
        // Превышение веса основного багажа
        const baggageWeightExcess = Math.max(0, numBaggageWeight - allowedBaggageWeight + numOverweightKg);
        
        // Превышение количества мест основного багажа
        const baggageCountExcess = Math.max(0, numBaggageCount - allowedBaggageCount);
        
        // Превышение веса ручной клади
        const carryOnWeightExcess = Math.max(0, numCarryOnWeight - allowedCarryOnWeight);
        
        // Превышение количества мест ручной клади
        const carryOnCountExcess = Math.max(0, numCarryOnCount - allowedCarryOnCount);
        
        // --- Расчет дополнительных предметов ---
        let extraItemsTotalWeight = 0;
        let extraItemsData = [];
        
        if (hasExtraItems && extraItems.length > 0) {
          extraItems.forEach(itemId => {
            const item = extraItemsList.find(i => i.id === itemId);
            if (item) {
              extraItemsTotalWeight += item.weight;
              extraItemsData.push({
                ...item,
                allowed: itemId === 'wheelchair' || itemId === 'medical_equipment' // Медицинское оборудование и инвалидные коляски обычно бесплатны
              });
            }
          });
        }
        
        // --- Расчет стоимости перевеса ---
        const extraBaggageFees = airlineData.extraBaggageFees[destinationType];
        
        // Стоимость перевеса багажа
        let overweightFee = 0;
        if (baggageWeightExcess > 0) {
          overweightFee = extraBaggageFees.overweight.base + 
            extraBaggageFees.overweight.perKg * baggageWeightExcess;
        }
        
        // Стоимость дополнительных мест багажа
        const extraPieceFee = baggageCountExcess * extraBaggageFees.extraPiece;
        
        // Стоимость перевеса ручной клади (обычно это перевод в багаж)
        let carryOnExcessFee = 0;
        if (carryOnWeightExcess > 0 || carryOnCountExcess > 0) {
          carryOnExcessFee = extraBaggageFees.extraPiece;
        }
        
        // Стоимость дополнительных предметов (если не разрешены бесплатно)
        let extraItemsFee = 0;
        extraItemsData.forEach(item => {
          if (!item.allowed) {
            extraItemsFee += extraBaggageFees.extraPiece;
          }
        });
        
        // Итоговая стоимость всех превышений
        const totalExcessFee = overweightFee + extraPieceFee + carryOnExcessFee + extraItemsFee;
        
        // --- Итоговые результаты расчета ---
        const finalResults = {
          // Основные данные авиакомпании
          airline: airlineData,
          
          // Допустимые нормы
          allowedBaggageWeight,
          allowedBaggageCount,
          allowedCarryOnWeight,
          allowedCarryOnCount,
          
          // Расчеты превышений
          baggageWeightExcess,
          baggageCountExcess,
          carryOnWeightExcess,
          carryOnCountExcess,
          
          // Данные о перевесе
          overweightIncluded: includeOverweight,
          overweightKg: numOverweightKg,
          
          // Дополнительные предметы
          extraItems: extraItemsData,
          extraItemsTotalWeight,
          
          // Стоимость превышений
          overweightFee,
          extraPieceFee,
          carryOnExcessFee,
          extraItemsFee,
          totalExcessFee,
          
          // Бонусы лояльности
          loyaltyBonus,
          
          // Специальные условия
          specialConditions,
          
          // Итоговые лимиты с учетом всех особенностей
          effectiveBaggageWeight: allowedBaggageWeight,
          effectiveBaggageCount: allowedBaggageCount,
          effectiveCarryOnWeight: allowedCarryOnWeight,
          effectiveCarryOnCount: allowedCarryOnCount,
          
          // Рекомендации
          hasExcess: (baggageWeightExcess > 0 || baggageCountExcess > 0 || 
                       carryOnWeightExcess > 0 || carryOnCountExcess > 0),
          recommendations: []
        };
        
        // Добавление рекомендаций
        if (finalResults.hasExcess) {
          if (baggageWeightExcess > 0) {
            finalResults.recommendations.push({
              type: 'warning',
              text: `Превышение веса багажа на ${baggageWeightExcess} кг. Рекомендуется уменьшить вес или оплатить перевес.`
            });
          }
          
          if (baggageCountExcess > 0) {
            finalResults.recommendations.push({
              type: 'warning',
              text: `Превышение количества мест багажа на ${baggageCountExcess}. Потребуется оплата за дополнительное место.`
            });
          }
          
          if (carryOnWeightExcess > 0) {
            finalResults.recommendations.push({
              type: 'error',
              text: `Превышение веса ручной клади на ${carryOnWeightExcess} кг. Часть предметов придется сдать в багаж.`
            });
          }
          
          if (carryOnCountExcess > 0) {
            finalResults.recommendations.push({
              type: 'error',
              text: `Превышение количества мест ручной клади на ${carryOnCountExcess}. Перенесите вещи или сдайте в багаж.`
            });
          }
        } else {
          finalResults.recommendations.push({
            type: 'success',
            text: 'Ваш багаж соответствует нормам выбранной авиакомпании.'
          });
        }
        
        // Проверка на другие авиакомпании, где может быть выгоднее
        const alternativeAirlines = airlinesData
          .filter(a => a.id !== airline)
          .map(a => {
            const altAllowance = a.baggageAllowance[destinationType][travelClass];
            return {
              airline: a,
              allowedBaggageWeight: altAllowance.weight,
              allowedBaggageCount: altAllowance.count,
              extraPieceFee: a.extraBaggageFees[destinationType].extraPiece,
              overweightFee: a.extraBaggageFees[destinationType].overweight,
              betterOption: altAllowance.weight > baggageAllowance.weight || 
                            altAllowance.count > baggageAllowance.count ||
                            a.extraBaggageFees[destinationType].overweight.perKg < 
                            airlineData.extraBaggageFees[destinationType].overweight.perKg
            };
          })
          .filter(a => a.betterOption);
          
        finalResults.alternativeAirlines = alternativeAirlines;
        
        if (alternativeAirlines.length > 0) {
          const bestAlternative = alternativeAirlines[0];
          finalResults.recommendations.push({
            type: 'info',
            text: `Рассмотрите ${bestAlternative.airline.name} для более выгодных условий по багажу.`
          });
        }

        setResults(finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, airlinesData, extraItemsList, setErrors, setResults, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      airline: 'aeroflot',
      travelClass: 'economy',
      destinationType: 'domestic',
      passengerType: 'adult',
      baggageWeight: '20',
      carryOnWeight: '5',
      carryOnCount: '1',
      baggageCount: '1',
      hasLoyaltyStatus: false,
      loyaltyStatus: 'standard',
      hasExtraItems: false,
      extraItems: [],
      includeOverweight: false,
      overweightKg: '5'
    });
    setResults(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateLuggage();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-purple-500" />
          Рассчитать допустимую массу багажа
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Параметры авиакомпании и полета */}
        <AirlineParamsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          airlinesData={airlinesData}
          travelClasses={travelClasses}
          destinationTypes={destinationTypes}
        />

        {/* Параметры пассажира */}
        <PassengerParamsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          passengerTypes={passengerTypes}
        />

        {/* Параметры багажа */}
        <BaggageParamsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Дополнительные предметы */}
        <ExtraItemsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          handleExtraItemToggle={handleExtraItemToggle}
          errors={errors} 
          extraItemsList={extraItemsList}
        />

        {/* Программа лояльности */}
        <LoyaltyParamsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          loyaltyStatuses={loyaltyStatuses}
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Scale className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Расчет...' : 'Рассчитать багаж'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;